import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { Signup, ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthPages";
import Profile from "@/pages/Profile";
import Privacy from "@/pages/Privacy";
import Staples from "@/pages/Staples";
import Feedback from "@/pages/Feedback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Grocery from "@/pages/Grocery";
import Maintenance from "@/pages/Maintenance";
import Wishlist from "@/pages/Wishlist";
import Warranties from "@/pages/Warranties";
import Plants from "@/pages/Plants";
import Insurance from "@/pages/Insurance";
import Bills from "@/pages/Bills";
import Providers from "@/pages/Providers";
import Chores from "@/pages/Chores";
import Household from "@/pages/Household";
import Vendors from "@/pages/Vendors";
import GasBottles from "@/pages/GasBottles";
import Vehicles from "@/pages/Vehicles";
import Pets from "@/pages/Pets";
import Reminders from "@/pages/Reminders";
import Calendar from "@/pages/Calendar";
import Expenses from "@/pages/Expenses";
import UseSoon from "@/pages/UseSoon";
import SavedRecipes from "@/pages/SavedRecipes";
import StatusPage from "@/pages/StatusPage";
import Upgrade from "@/pages/Upgrade";
import BillingSuccess from "@/pages/BillingSuccess";
import SetupCuisines from "@/pages/SetupCuisines";
import SetupVendors from "@/pages/SetupVendors";
import SetupStaples from "@/pages/SetupStaples";
// MealPlan page removed from routing in v27.16 — source at src/pages/MealPlan.jsx
// remains for future reference. Backend router in routes/meal_plan.py is also
// no longer registered in server.py. Restore by re-adding the import + Route
// below AND the register_meal_plan_routes() block in server.py.

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground font-display text-2xl italic">Setting the hearth…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/setup/cuisines" element={<Protected><SetupCuisines /></Protected>} />
            <Route path="/setup/vendors" element={<Protected><SetupVendors /></Protected>} />
            <Route path="/setup/staples" element={<Protected><SetupStaples /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/grocery" element={<Grocery />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/warranties" element={<Warranties />} />
              <Route path="/plants" element={<Plants />} />
              <Route path="/insurance" element={<Insurance />} />
              <Route path="/bills" element={<Bills />} />
              <Route path="/providers" element={<Providers />} />
              <Route path="/chores" element={<Chores />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/gas-bottles" element={<GasBottles />} />
              <Route path="/vehicles" element={<Vehicles />} />
              <Route path="/pets" element={<Pets />} />
              <Route path="/reminders" element={<Reminders />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/use-soon" element={<UseSoon />} />
              <Route path="/staples" element={<Staples />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/saved-recipes" element={<SavedRecipes />} />
              {/* /meal-plan route removed in v27.16 — restore by re-adding
                  the import above and this Route line if you bring the page back. */}
              <Route path="/household" element={<Household />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/profile" element={<Profile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
